import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { labelRect, runPoints, toFrame, type BendFrame, type ConnectorPath } from '../../doc/connector';
import type { Point, Rect } from '../../doc/geometry';
import type { ConnectorItem } from '../../doc/schema';
import { useBoard, usePane, usePaneStore } from '../context';
import { useLiveConnector } from '../items/ConnectorItem';
import { screenToWorld, worldToScreen } from '../viewport/camera';
import { allowedTarget, attachTargetAt, endAt } from './connectors';

type Drag =
  | { kind: 'end'; which: 'start' | 'end'; pointerId: number; moved: boolean }
  | { kind: 'bend'; index: number; bend: number[]; frame: BendFrame; pointerId: number; moved: boolean }
  | { kind: 'mid'; a: Point; b: Point; axis: 'x' | 'y'; pointerId: number; moved: boolean };

/**
 * Where each run's add-a-bend dot goes: halfway, or a quarter in from either end when the label
 * covers the middle (the label is dragged there itself); null when it covers the whole run.
 */
function addSpots(path: ConnectorPath, label: Rect | null): (Point | null)[] {
  const mids = runPoints(path, 0.5);
  if (!label) return mids;
  const covered = (q: Point) => q.x >= label.x && q.x <= label.x + label.w && q.y >= label.y && q.y <= label.y + label.h;
  if (!mids.some(covered)) return mids;
  const late = runPoints(path, 0.75);
  const early = runPoints(path, 0.25);
  return mids.map((m, i) => [m, late[i]!, early[i]!].find((q) => !covered(q)) ?? null);
}

/**
 * Handles of a selected connector, in screen space: its two ends (drag one onto an item to
 * attach it, at a side dot for a fixed point; into empty space, or with Alt, to let go), its
 * bend points (drag to move, double-click to remove), a dot halfway along each run of a straight
 * or curved connector (drag it to add a bend there), and an elbow's middle run (drag to slide it).
 * Its label is no handle here: it is dragged along the path itself (connector/ConnectorLabel.tsx).
 * Edits show live through the pane's connector draft and are written once, on release.
 */
export function ConnectorHandles({ item }: { item: ConnectorItem }) {
  const board = useBoard();
  const pane = usePaneStore();
  const camera = usePane((s) => s.camera);
  const { props, geo } = useLiveConnector(item);
  const drag = useRef<Drag | null>(null);

  const toWorld = (e: ReactPointerEvent): Point => {
    const host = (e.currentTarget as HTMLElement).closest('.viewport');
    const r = host?.getBoundingClientRect();
    const local = r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: e.clientX, y: e.clientY };
    return screenToWorld(pane.getState().camera, local);
  };

  const begin = (e: ReactPointerEvent, d: Drag) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = d;
  };

  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const world = toWorld(e);
    const s = pane.getState();
    d.moved = true;
    if (d.kind === 'end') {
      const other = d.which === 'start' ? item.props.end : item.props.start;
      const target = allowedTarget(e.altKey ? null : attachTargetAt(board, world, s.camera.zoom), other);
      s.setConnectTarget(target);
      s.setConnectorDraft(item.id, { [d.which]: endAt(board, target, world) });
    } else if (d.kind === 'bend') {
      const bend = d.bend.slice();
      [bend[d.index * 2], bend[d.index * 2 + 1]] = toFrame(d.frame, world);
      s.setConnectorDraft(item.id, { bend });
    } else {
      const span = d.axis === 'x' ? d.b.x - d.a.x : d.b.y - d.a.y;
      if (Math.abs(span) < 1e-6) return;
      const mid = (d.axis === 'x' ? world.x - d.a.x : world.y - d.a.y) / span;
      s.setConnectorDraft(item.id, { mid: Math.max(-2, Math.min(3, mid)) });
    }
  };

  const onUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    const s = pane.getState();
    const draft = s.connectorDraft[item.id];
    if (d.moved && draft && e.type === 'pointerup') board.updateItem(item.id, { props: draft });
    s.setConnectTarget(null);
    s.setConnectorDraft(item.id, null);
  };

  const removeBend = (index: number) => {
    const bend = item.props.bend.slice();
    bend.splice(index * 2, 2);
    board.updateItem(item.id, { props: { bend } });
  };

  const at = (p: Point) => {
    const q = worldToScreen(camera, p);
    return { left: q.x, top: q.y };
  };
  const events = { onPointerMove: onMove, onPointerUp: onUp, onPointerCancel: onUp };
  const bends = props.route === 'elbow' ? [] : geo.bends;
  const label = labelRect(props, geo);
  const pad = 6 / camera.zoom;
  const adds = props.route === 'elbow' ? [] : addSpots(geo.path, label && { x: label.x - pad, y: label.y - pad, w: label.w + pad * 2, h: label.h + pad * 2 });
  // a run of an elbow route can be too short to carry a handle of its own
  const showMid = !!geo.elbow && Math.abs(geo.elbow.axis === 'x' ? geo.elbow.a.y - geo.elbow.b.y : geo.elbow.a.x - geo.elbow.b.x) * camera.zoom > 12;

  return (
    <div className="sel-conn">
      {adds.map((p, i) => p && (
        <div
          key={`add-${i}`}
          className="handle conn-add"
          title="Drag to bend the connector here"
          style={at(p)}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            // a new bend point between the ends of this run, then dragged like any other
            const bend = item.props.bend.slice();
            bend.splice(i * 2, 0, ...toFrame(geo.frame, p));
            pane.getState().setConnectorDraft(item.id, { bend });
            begin(e, { kind: 'bend', index: i, bend, frame: geo.frame, pointerId: e.pointerId, moved: false });
          }}
          {...events}
        />
      ))}
      {bends.map((p, i) => (
        <div
          key={`bend-${i}`}
          className="handle conn-bend"
          title="Drag to bend; double-click to remove the point"
          style={at(p)}
          onPointerDown={(e) => begin(e, { kind: 'bend', index: i, bend: item.props.bend.slice(), frame: geo.frame, pointerId: e.pointerId, moved: false })}
          onDoubleClick={(e) => {
            e.stopPropagation();
            removeBend(i);
          }}
          {...events}
        />
      ))}
      {showMid && geo.elbow && (
        <div
          className="handle conn-mid"
          data-cursor={geo.elbow.axis === 'x' ? 'ew' : 'ns'}
          title="Drag to move the middle run"
          style={at(geo.elbow.at)}
          onPointerDown={(e) => begin(e, { kind: 'mid', a: geo.elbow!.a, b: geo.elbow!.b, axis: geo.elbow!.axis, pointerId: e.pointerId, moved: false })}
          {...events}
        />
      )}
      {(['start', 'end'] as const).map((which) => (
        <div
          key={which}
          className={`handle conn-end${props[which].item ? ' is-attached' : ''}`}
          title={`Drag onto an item to attach the ${which} (on a side dot: to that point); Alt lets go`}
          style={at(geo[which])}
          onPointerDown={(e) => begin(e, { kind: 'end', which, pointerId: e.pointerId, moved: false })}
          {...events}
        />
      ))}
    </div>
  );
}
